import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    VocabularyWord,
    CreateWordRequest,
    UpdateWordRequest,
    VocabularyFilters,
    PaginationParams,
    VocabularyResponse,
    PracticeResult,
} from '@/services/vocabularyService';

// === WORDS ACTIONS ===
export const fetchWordsRequest = createAction<{ filters?: VocabularyFilters; pagination?: PaginationParams }>(
    'words/fetchWordsRequest',
);
export const fetchWordsSuccess = createAction<VocabularyResponse>('words/fetchWordsSuccess');
export const fetchWordsFailure = createAction<string>('words/fetchWordsFailure');

export const fetchWordByIdRequest = createAction<string>('words/fetchWordByIdRequest');
export const fetchWordByIdSuccess = createAction<VocabularyWord>('words/fetchWordByIdSuccess');
export const fetchWordByIdFailure = createAction<string>('words/fetchWordByIdFailure');

export const createWordRequest = createAction<CreateWordRequest>('words/createWordRequest');
export const createWordSuccess = createAction<VocabularyWord>('words/createWordSuccess');
export const createWordFailure = createAction<string>('words/createWordFailure');

export const createBulkWordsRequest = createAction<CreateWordRequest[]>('words/createBulkWordsRequest');
export const createBulkWordsSuccess = createAction<VocabularyWord[]>('words/createBulkWordsSuccess');
export const createBulkWordsFailure = createAction<string>('words/createBulkWordsFailure');

export const updateWordRequest = createAction<UpdateWordRequest>('words/updateWordRequest');
export const updateWordSuccess = createAction<VocabularyWord>('words/updateWordSuccess');
export const updateWordFailure = createAction<string>('words/updateWordFailure');

export const deleteWordRequest = createAction<string>('words/deleteWordRequest');
export const deleteWordSuccess = createAction<string>('words/deleteWordSuccess');
export const deleteWordFailure = createAction<string>('words/deleteWordFailure');

export const toggleFavoriteRequest = createAction<string>('words/toggleFavoriteRequest');
export const toggleFavoriteSuccess = createAction<VocabularyWord>('words/toggleFavoriteSuccess');
export const toggleFavoriteFailure = createAction<string>('words/toggleFavoriteFailure');

export const toggleBookmarkRequest = createAction<string>('words/toggleBookmarkRequest');
export const toggleBookmarkSuccess = createAction<VocabularyWord>('words/toggleBookmarkSuccess');
export const toggleBookmarkFailure = createAction<string>('words/toggleBookmarkFailure');

export const fetchFavoriteWordsRequest = createAction<PaginationParams | undefined>('words/fetchFavoriteWordsRequest');
export const fetchFavoriteWordsSuccess = createAction<VocabularyResponse>('words/fetchFavoriteWordsSuccess');
export const fetchFavoriteWordsFailure = createAction<string>('words/fetchFavoriteWordsFailure');

export const fetchBookmarkedWordsRequest = createAction<PaginationParams | undefined>(
    'words/fetchBookmarkedWordsRequest',
);
export const fetchBookmarkedWordsSuccess = createAction<VocabularyResponse>('words/fetchBookmarkedWordsSuccess');
export const fetchBookmarkedWordsFailure = createAction<string>('words/fetchBookmarkedWordsFailure');

// === PRACTICE ACTIONS ===
export const practiceWordRequest = createAction<{ wordId: string; type: string }>('words/practiceWordRequest');
export const practiceWordSuccess = createAction<PracticeResult>('words/practiceWordSuccess');
export const practiceWordFailure = createAction<string>('words/practiceWordFailure');

export const submitPracticeResultRequest = createAction<{ wordId: string; result: Omit<PracticeResult, 'wordId'> }>(
    'words/submitPracticeResultRequest',
);
export const submitPracticeResultSuccess = createAction<PracticeResult>('words/submitPracticeResultSuccess');
export const submitPracticeResultFailure = createAction<string>('words/submitPracticeResultFailure');

export const fetchPracticeHistoryRequest = createAction<{ wordId?: string; pagination?: PaginationParams }>(
    'words/fetchPracticeHistoryRequest',
);
export const fetchPracticeHistorySuccess = createAction<{
    sessions: any[];
    total: number;
    page: number;
    totalPages: number;
}>('words/fetchPracticeHistorySuccess');
export const fetchPracticeHistoryFailure = createAction<string>('words/fetchPracticeHistoryFailure');

export const fetchTranscriptDataRequest = createAction<{ id: string; type: string }>(
    'words/fetchTranscriptDataRequest',
);
export const fetchTranscriptDataSuccess = createAction<any>('words/fetchTranscriptDataSuccess');
export const fetchTranscriptDataFailure = createAction<string>('words/fetchTranscriptDataFailure');

// === STATE INTERFACE ===
export interface WordsState {
    words: VocabularyWord[];
    selectedWord: VocabularyWord | null;
    transcriptData: any;
    // Practice
    practiceHistory: any[];
    practiceStats: any;

    // Filters and Pagination
    filters: VocabularyFilters;
    pagination: {
        page: number;
        totalPages: number;
        total: number;
        limit: number;
    };

    // Loading States
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;

    // Error States
    error: string | null;
}

const initialState: WordsState = {
    words: [],
    selectedWord: null,
    transcriptData: null,
    practiceHistory: [],
    practiceStats: null,
    filters: {},
    pagination: {
        page: 1,
        totalPages: 1,
        total: 0,
        limit: 20,
    },
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
};

const wordsSlice = createSlice({
    name: 'words',
    initialState,
    reducers: {
        // Local state management
        setFilters: (state, action: PayloadAction<VocabularyFilters>) => {
            state.filters = action.payload;
        },
        clearFilters: (state) => {
            state.filters = {};
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.pagination.page = action.payload;
        },
        setLimit: (state, action: PayloadAction<number>) => {
            state.pagination.limit = action.payload;
        },
        setSelectedWord: (state, action: PayloadAction<VocabularyWord | null>) => {
            state.selectedWord = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetState: (state) => {
            state.words = [];
            state.selectedWord = null;
            state.transcriptData = null;
            state.practiceHistory = [];
            state.practiceStats = null;
            state.filters = {};
            state.pagination = {
                page: 1,
                totalPages: 1,
                total: 0,
                limit: 20,
            };
            state.isLoading = false;
            state.isCreating = false;
            state.isUpdating = false;
            state.isDeleting = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH WORDS ===
            .addCase(fetchWordsRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchWordsSuccess, (state, action) => {
                console.log('action', action);
                state.isLoading = false;
                state.words = action.payload.results;
                state.pagination = {
                    page: 1, // Default since page doesn't exist in VocabularyResponse
                    totalPages: Math.ceil(action.payload.count / state.pagination.limit),
                    total: action.payload.count,
                    limit: state.pagination.limit,
                };
            })
            .addCase(fetchWordsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // === FETCH WORD BY ID ===
            .addCase(fetchWordByIdRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchWordByIdSuccess, (state, action) => {
                state.isLoading = false;
                state.selectedWord = action.payload;
                // Update word in words array if it exists
                const index = state.words.findIndex((word) => word.id === action.payload.id);
                if (index !== -1) {
                    state.words[index] = action.payload;
                }
            })
            .addCase(fetchWordByIdFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // === CREATE WORD ===
            .addCase(createWordRequest, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createWordSuccess, (state, action) => {
                state.isCreating = false;
                state.words.unshift(action.payload);
                state.pagination.total += 1;
            })
            .addCase(createWordFailure, (state, action) => {
                state.isCreating = false;
                state.error = action.payload;
            })

            // === CREATE BULK WORDS ===
            .addCase(createBulkWordsRequest, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createBulkWordsSuccess, (state, action) => {
                state.isCreating = false;
                state.words.unshift(...action.payload);
                state.pagination.total += action.payload.length;
            })
            .addCase(createBulkWordsFailure, (state, action) => {
                state.isCreating = false;
                state.error = action.payload;
            })

            // === UPDATE WORD ===
            .addCase(updateWordRequest, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateWordSuccess, (state, action) => {
                state.isUpdating = false;
                const index = state.words.findIndex((word) => word.id === action.payload.id);
                if (index !== -1) {
                    state.words[index] = action.payload;
                }
                if (state.selectedWord?.id === action.payload.id) {
                    state.selectedWord = action.payload;
                }
            })
            .addCase(updateWordFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            })

            // === DELETE WORD ===
            .addCase(deleteWordRequest, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteWordSuccess, (state, action) => {
                state.isDeleting = false;
                state.words = state.words.filter((word) => word.id.toString() !== action.payload);
                state.pagination.total = Math.max(0, state.pagination.total - 1);
                if (state.selectedWord?.id.toString() === action.payload) {
                    state.selectedWord = null;
                }
            })
            .addCase(deleteWordFailure, (state, action) => {
                state.isDeleting = false;
                state.error = action.payload;
            })

            // === TOGGLE FAVORITE ===
            .addCase(toggleFavoriteRequest, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(toggleFavoriteSuccess, (state, action) => {
                state.isUpdating = false;
                const index = state.words.findIndex((word) => word.id === action.payload.id);
                if (index !== -1) {
                    state.words[index] = action.payload;
                }
                if (state.selectedWord?.id === action.payload.id) {
                    state.selectedWord = action.payload;
                }
            })
            .addCase(toggleFavoriteFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            })

            // === TOGGLE BOOKMARK ===
            .addCase(toggleBookmarkRequest, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(toggleBookmarkSuccess, (state, action) => {
                state.isUpdating = false;
                const index = state.words.findIndex((word) => word.id === action.payload.id);
                if (index !== -1) {
                    state.words[index] = action.payload;
                }
                if (state.selectedWord?.id === action.payload.id) {
                    state.selectedWord = action.payload;
                }
            })
            .addCase(toggleBookmarkFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            })

            // === FETCH FAVORITE WORDS ===
            .addCase(fetchFavoriteWordsRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchFavoriteWordsSuccess, (state, action) => {
                state.isLoading = false;
                state.words = action.payload.results;
                state.pagination = {
                    page: 1, // Default since page doesn't exist in VocabularyResponse
                    totalPages: Math.ceil(action.payload.count / state.pagination.limit),
                    total: action.payload.count,
                    limit: state.pagination.limit,
                };
            })
            .addCase(fetchFavoriteWordsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // === FETCH BOOKMARKED WORDS ===
            .addCase(fetchBookmarkedWordsRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchBookmarkedWordsSuccess, (state, action) => {
                state.isLoading = false;
                state.words = action.payload.results;
                state.pagination = {
                    page: 1, // Default since page doesn't exist in VocabularyResponse
                    totalPages: Math.ceil(action.payload.count / state.pagination.limit),
                    total: action.payload.count,
                    limit: state.pagination.limit,
                };
            })
            .addCase(fetchBookmarkedWordsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // === PRACTICE WORD ===
            .addCase(practiceWordRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(practiceWordSuccess, (state, action) => {
                state.isLoading = false;
                state.practiceHistory.unshift(action.payload);
            })
            .addCase(practiceWordFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // === SUBMIT PRACTICE RESULT ===
            .addCase(submitPracticeResultRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(submitPracticeResultSuccess, (state, action) => {
                state.isLoading = false;
                const index = state.practiceHistory.findIndex((result) => result.wordId === action.payload.wordId);
                if (index !== -1) {
                    state.practiceHistory[index] = action.payload;
                } else {
                    state.practiceHistory.unshift(action.payload);
                }
            })
            .addCase(submitPracticeResultFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // === FETCH PRACTICE HISTORY ===
            .addCase(fetchPracticeHistoryRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPracticeHistorySuccess, (state, action) => {
                state.isLoading = false;
                state.practiceHistory = action.payload.sessions;
                state.pagination = {
                    ...state.pagination,
                    page: action.payload.page,
                    totalPages: action.payload.totalPages,
                    total: action.payload.total,
                };
            })
            .addCase(fetchPracticeHistoryFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // === FETCH TRANSCRIPT DATA ===
            .addCase(fetchTranscriptDataRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTranscriptDataSuccess, (state, action) => {
                state.isLoading = false;
                state.transcriptData = action.payload;
            })
            .addCase(fetchTranscriptDataFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { setFilters, clearFilters, setPage, setLimit, setSelectedWord, clearError, resetState } =
    wordsSlice.actions;

export default wordsSlice.reducer;
